import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, RefreshCw, Filter } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { http, api } from "@/lib/api";
import IncidentRow from "@/components/incidents/IncidentRow";
import IncidentDetail from "@/components/incidents/IncidentDetail";
import IncidentAnalytics from "@/components/incidents/IncidentAnalytics";
import NewIncidentDialog from "@/components/incidents/NewIncidentDialog";

const SEV_FILTERS = ["all", "critical", "high", "medium", "low"];
const STATUS_FILTERS = ["all", "ack", "dispatched", "resolved", "reviewed"];
const TEAM_FILTERS = ["all", "security", "police", "medical", "fire", "facilities"];

const titleCase = (s) =>
  s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1);

export default function Incidents() {
  const { alerts } = useOutletContext();
  const [incidents, setIncidents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [filterSev, setFilterSev] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTeam, setFilterTeam] = useState("all");

  const load = async () => {
    setRefreshing(true);
    try {
      const [inc, ana] = await Promise.all([
        api.incidents(),
        http.get("/incidents/analytics").then((r) => r.data),
      ]);
      setIncidents(inc.incidents ?? []);
      setAnalytics(ana);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    return incidents.filter((i) => {
      if (filterSev !== "all" && i.severity !== filterSev) return false;
      if (filterStatus !== "all" && i.status !== filterStatus) return false;
      if (filterTeam !== "all" && i.team_type !== filterTeam) return false;
      return true;
    });
  }, [incidents, filterSev, filterStatus, filterTeam]);

  // Keep selection in sync
  const selected = useMemo(
    () => incidents.find((i) => i.id === selectedId) || filtered[0],
    [incidents, selectedId, filtered]
  );

  useEffect(() => {
    if (selected && selected.id !== selectedId) setSelectedId(selected.id);
  }, [selected, selectedId]);

  const handleUpdated = (updated) => {
    setIncidents((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    // refresh analytics in background
    http.get("/incidents/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };

  const handleCreated = (created) => {
    setIncidents((prev) => [created, ...prev]);
    setSelectedId(created.id);
    http.get("/incidents/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Slim title row */}
      <div className="h-10 flex items-center justify-between px-4 border-b border-border bg-[hsl(var(--surface))] shrink-0">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display font-black uppercase tracking-tight text-sm leading-none">
            Incidents & Emergency
          </h1>
          <span className="micro-label hidden sm:inline">Inbox · Workflow · Analytics</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setDialogOpen(true)}
            className="btn-tac-primary !px-2 !py-1.5"
            data-testid="new-incident-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New Incident
          </button>
          <button
            onClick={load}
            className="btn-tac !px-2 !py-1.5"
            data-testid="incidents-refresh-btn"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <Tabs defaultValue="inbox" className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <TabsList className="px-2 pt-2 pb-0 bg-transparent border-b border-border h-auto rounded-none justify-start gap-1 shrink-0">
          <TabsTrigger
            value="inbox"
            data-testid="tab-inbox"
            className="rounded-none border border-border border-b-0 data-[state=active]:bg-[hsl(var(--surface))] data-[state=active]:border-b-[hsl(var(--surface))] data-[state=active]:!shadow-none px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold"
          >
            Inbox
          </TabsTrigger>
          <TabsTrigger
            value="analytics"
            data-testid="tab-analytics"
            className="rounded-none border border-border border-b-0 data-[state=active]:bg-[hsl(var(--surface))] data-[state=active]:border-b-[hsl(var(--surface))] data-[state=active]:!shadow-none px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold"
          >
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="flex-1 m-0 overflow-hidden">
          <div className="h-full flex flex-col xl:flex-row min-h-0">
            {/* List pane */}
            <div className="xl:w-[420px] xl:shrink-0 border-b xl:border-b-0 xl:border-r border-border flex flex-col min-h-0">
              {/* Filters — single row of compact dropdowns */}
              <div className="px-3 py-2 border-b border-border flex items-center gap-2 shrink-0">
                <Filter className="w-3 h-3 text-muted-foreground shrink-0" />
                <FilterSelect
                  label="Severity"
                  value={filterSev}
                  onChange={setFilterSev}
                  options={SEV_FILTERS}
                  testid="filter-sev"
                />
                <FilterSelect
                  label="Status"
                  value={filterStatus}
                  onChange={setFilterStatus}
                  options={STATUS_FILTERS}
                  testid="filter-status"
                />
                <FilterSelect
                  label="Team"
                  value={filterTeam}
                  onChange={setFilterTeam}
                  options={TEAM_FILTERS}
                  testid="filter-team"
                />
              </div>
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-border shrink-0">
                <span className="h-widget">Inbox</span>
                <span className="font-mono text-[10px] text-muted-foreground" data-testid="incident-count">
                  {filtered.length} / {incidents.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0" data-testid="incident-list">
                {filtered.length === 0 && (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    No incidents match these filters.
                  </div>
                )}
                {filtered.map((i) => (
                  <IncidentRow
                    key={i.id}
                    incident={i}
                    selected={selected?.id === i.id}
                    onSelect={(inc) => setSelectedId(inc.id)}
                  />
                ))}
              </div>
            </div>

            {/* Detail pane */}
            <div className="flex-1 min-h-0 bg-[hsl(var(--surface))]">
              <IncidentDetail incident={selected} onUpdated={handleUpdated} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="flex-1 m-0 overflow-y-auto p-2">
          <IncidentAnalytics analytics={analytics} />
        </TabsContent>
      </Tabs>

      <NewIncidentDialog
        events={alerts}
        existingEventIds={incidents.map((i) => i.event_id)}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={handleCreated}
      />
    </div>
  );
}

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div className="flex items-center gap-1.5 flex-1 min-w-0">
    <span className="micro-label shrink-0">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className="h-7 px-2 text-[11px] font-medium tracking-tight bg-transparent border-border rounded-none flex-1 min-w-0"
        data-testid={`${testid}-trigger`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-none">
        {options.map((o) => (
          <SelectItem
            key={o}
            value={o}
            className="text-[11px] tracking-tight"
            data-testid={`${testid}-${o}`}
          >
            {titleCase(o)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);
